// // src/components/SideMenu/SideMenu.tsx
import React, { FC, useCallback, useEffect } from 'react';
import './SideMenu.less';
import { Layout, Tooltip } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getActiveList, getCanCurate, getUserData } from '../../views/auth/slice/selectors';
import { Path } from '../../constants/Path';
import { Roles } from '../../constants/Roles';
import { GAMES, GameGroup, getGameGroup } from '../../constants/Games';
import useGameGroup from '../../hooks/useGameGroup';
import { logout } from '../../views/auth/slice/actions';

import MenuButton from '../MenuButton/MenuButton';
import classes from './SideMenu.module.less';
import Sizes from '../../constants/Sizes';

import studentsIcons from '../../assets/icons/menu/students';
import listsIcons from '../../assets/icons/menu/lists';
import catalogIcons from '../../assets/icons/menu/catalog';
import logoutIcons from '../../assets/icons/menu/logout';

const { Sider } = Layout;

// E — gry Easy, R — gry Regular. Co znaczą litery, mówi podpowiedź po najechaniu; na tablecie
// się ona nie pokaże.
const GAME_GROUP_OPTIONS = [
  { group: GameGroup.KIDDO, label: 'E' },
  { group: GameGroup.OLDER, label: 'R' },
];

const SideMenu: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useSelector(getUserData);
  const canCurate = useSelector(getCanCurate);
  const activeList = useSelector(getActiveList);
  const [gameGroup, setGameGroup] = useGameGroup();

  // Gra z drugiej grupy otwarta z zakładki albo z linku przestawia przełącznik, żeby była widoczna w menu.
  // Tylko przy zmianie adresu — przełączenie grupy w trakcie gry nie zostaje od razu cofnięte.
  useEffect(() => {
    const openGameGroup = getGameGroup(pathname);

    if (openGameGroup) {
      setGameGroup(openGameGroup);
    }
  }, [pathname, setGameGroup]);

  const onLogoutClick = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  return (
    <Sider className={classes.sider} width={Sizes.SIDE_MENU}>
      <div className={classes.sectionContainer}>
        <div className={classes.cockpit}>
          {
            user?.role === Roles.TEACHER && (
              <MenuButton
                icons={studentsIcons}
                text={t('common:menu.students')}
                to={Path.STUDENTS}
                style={{}}
              />
            )
          }
          {
            user?.role === Roles.TEACHER && (
              <MenuButton
                icons={listsIcons}
                text={t('common:menu.wordList')}
                to={Path.WORDS_LIST}
                style={{}}
              />
            )
          }
          {
            // Wpisy katalogu wzorcowych obrazków — tylko dla kuratora
            user?.role === Roles.TEACHER && canCurate && (
              <MenuButton
                icons={catalogIcons}
                text={t('common:menu.catalog')}
                to={Path.CATALOG}
                style={{}}
              />
            )
          }
          {
            user?.role === Roles.STUDENT && (
              <MenuButton
                icons={listsIcons}
                text={t('common:menu.wordList')}
                to={Path.MY_LISTS}
                style={{}}
              />
            )
          }
        </div>

        {/* Kreska nad grami zostaje także bez aktywnej listy */}
        <div className={classes.upperSeparator} />

        {
          // Bez aktywnej listy nie ma w co grać — znika i przełącznik grup, i gry
          !!activeList && (
            <>
              {/* Jedna podpowiedź dla całego przełącznika — osobna przy „E” zasłaniałaby „R” */}
              <Tooltip
                title={<>{t('common:menu.gamesKiddo')}<br />{t('common:menu.gamesOlder')}</>}
                trigger="hover"
                placement="right"
              >
                <div className={classes.gameGroupSwitch}>
                  {GAME_GROUP_OPTIONS.map(({ group, label }) => (
                    <button
                      key={group}
                      type="button"
                      aria-pressed={gameGroup === group}
                      className={classNames(classes.gameGroupOption, {
                        [classes.selected]: gameGroup === group,
                        [classes.bright]: group === GameGroup.KIDDO,
                      })}
                      onClick={() => setGameGroup(group)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Tooltip>

              {GAMES[gameGroup].map((game) => (
                <MenuButton
                  key={game.path}
                  icons={game.icons}
                  text={t(game.text)}
                  to={game.path}
                  number={game.number}
                  label={game.label}
                  bright={gameGroup === GameGroup.KIDDO}
                  accent={game.accent}
                />
              ))}
            </>
          )
        }
      </div>

      <div className={classes.lastSection}>
        <div className={classes.downSeparator} />

        <MenuButton
          icons={logoutIcons}
          text={t('common:menu.logout')}
          onClick={onLogoutClick}
          style={{}}
        />
      </div>
    </Sider>
  );
};

export default SideMenu;
